import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Product } from '@/api/types';
import { isMeterProduct, isTeloneProduct } from '@/pages/PublicCheckoutPage';
import { AnnouncementBar, Footer, IntroPanel, PartnersStrip, WhatsAppBanner, WhyChooseUs } from '@/components/PublicExtras';

type Category = 'ALL' | 'AIRTIME' | 'BUNDLES' | 'ZESA' | 'TELONE';

/**
 * The public landing page — what a visitor sees at "/" before signing in.
 *
 * Structurally modelled on magetsi.co.zw's homepage end to end (announcement bar, header, hero
 * with a search card and scrollable filter chips, service grid, "why choose us" cards, a contact
 * banner, a partners strip, footer) but with our own name, copy, visual identity and only the
 * services we actually support — no Magetsi branding, logo or text is reused.
 */
export function HomePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<Category>('ALL');
  const chipsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/api/public/checkout/products')
      .then((r) => r.json())
      .then((data) => setProducts((data.products ?? []).filter((p: Product) => p.enabled)))
      .catch(() => setProducts([]));
  }, []);

  const networks = useMemo(() => {
    const names = new Set<string>();
    (products ?? []).forEach((p) => {
      const n = p.network?.split(',')[0]?.trim();
      if (n && n.toLowerCase() !== 'allnetworks') names.add(n);
    });
    return [...names].sort();
  }, [products]);

  const [networkFilter, setNetworkFilter] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const filtered = (products ?? []).filter((p) => {
    if (category === 'ZESA' && !isMeterProduct(p)) return false;
    if (category === 'TELONE' && !isTeloneProduct(p)) return false;
    if (category === 'AIRTIME' && (p.requiresProductCode || isMeterProduct(p) || isTeloneProduct(p))) return false;
    if (category === 'BUNDLES' && (!p.requiresProductCode || isMeterProduct(p) || isTeloneProduct(p))) return false;
    if (networkFilter && !(p.network ?? '').includes(networkFilter)) return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  // Picked once per product load so it doesn't reshuffle on every render.
  const randomOrder = useMemo(() => {
    const order = new Map<number, number>();
    (products ?? []).forEach((p) => order.set(p.productId, Math.random()));
    return order;
  }, [products]);

  const isUnfiltered = category === 'ALL' && !query && !networkFilter;
  const truncated = isUnfiltered && !showAll;
  const displayed = truncated
    ? [...filtered].sort((a, b) => (randomOrder.get(a.productId) ?? 0) - (randomOrder.get(b.productId) ?? 0)).slice(0, 6)
    : filtered;

  useEffect(() => {
    if (!isUnfiltered) setShowAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, query, networkFilter]);

  const topZesaProduct = (products ?? []).find(isMeterProduct) ?? null;

  function openService(p: Product) {
    navigate('/pay', { state: { productId: p.productId } });
  }

  function scrollChips(delta: number) {
    chipsRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'system-ui, sans-serif' }}>
      <AnnouncementBar />

      <header className="pub-header" style={{
        background: 'white', color: '#1e2460', borderBottom: '1px solid #e5e7eb',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <span style={{ fontWeight: 700, fontSize: 18 }}>⚡ Recharge</span>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <Link to="/pay/status" style={{ color: '#1e2460', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            Check transaction
          </Link>
          <Link to="/login" style={{
            color: 'white', background: '#1e2460', padding: '8px 14px', borderRadius: 8,
            fontSize: 13, fontWeight: 600, textDecoration: 'none',
          }}>
            Staff sign in →
          </Link>
        </div>
      </header>

      {/* Hero: intro column + search/filter/grid card, same two-column layout as Magetsi's. */}
      <div className="pub-hero" style={{
        display: 'grid', gap: 40, alignItems: 'start',
      }}>
        <IntroPanel />

        <div>
          <div style={{ background: '#1e2460', borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <p style={{ color: 'white', fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
              Which service are you looking for?
            </p>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search bills, networks, products…"
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 8, border: 'none', fontSize: 15,
                marginBottom: networks.length ? 12 : 0,
              }}
            />
            {networks.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ScrollArrow dir="left" onClick={() => scrollChips(-160)} />
                <div
                  ref={chipsRef}
                  style={{
                    display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none',
                    paddingBottom: 2,
                  }}
                >
                  <Chip active={networkFilter === null} onClick={() => setNetworkFilter(null)}>All networks</Chip>
                  {networks.map((n) => (
                    <Chip key={n} active={networkFilter === n} onClick={() => setNetworkFilter(n)}>{n}</Chip>
                  ))}
                </div>
                <ScrollArrow dir="right" onClick={() => scrollChips(160)} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {([
              ['ALL', 'All'], ['AIRTIME', 'Airtime'], ['BUNDLES', 'Bundles'],
              ['ZESA', 'Electricity'], ['TELONE', 'Telone'],
            ] as [Category, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                style={{
                  padding: '6px 14px', borderRadius: 999, border: 'none', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                  background: category === key ? '#16a34a' : 'white',
                  color: category === key ? 'white' : '#444',
                  boxShadow: category === key ? 'none' : '0 1px 2px rgba(0,0,0,0.08)',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="pub-services" style={{ display: 'grid', gap: 10 }}>
            {displayed.map((p) => (
              <button
                key={p.productId}
                onClick={() => openService(p)}
                style={{
                  textAlign: 'left', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: '#1e2460', textTransform: 'uppercase' }}>
                  {isMeterProduct(p) ? 'Electricity' : isTeloneProduct(p) ? 'Telone'
                    : p.network ?? p.category ?? 'Service'}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{p.name}</span>
                <span style={{ fontSize: 12, color: '#888' }}>
                  {p.requiresProductCode ? 'Bundle' : 'Top-up'}{p.currency ? ` · ${p.currency}` : ''}
                </span>
              </button>
            ))}
            {products && filtered.length === 0 && (
              <p style={{ gridColumn: '1 / -1', fontSize: 13, color: '#888', textAlign: 'center' }}>
                Nothing matches that search.
              </p>
            )}
            {!products && (
              <p style={{ gridColumn: '1 / -1', fontSize: 13, color: '#888', textAlign: 'center' }}>Loading…</p>
            )}
          </div>

          {truncated && filtered.length > displayed.length && (
            <button
              onClick={() => setShowAll(true)}
              style={{
                width: '100%', marginTop: 10, padding: '10px 0', borderRadius: 10,
                border: '1px solid #d1d5db', background: 'white', color: '#1e2460',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              View all {filtered.length} services
            </button>
          )}

          {topZesaProduct && (
            <button
              onClick={() => openService(topZesaProduct)}
              style={{
                marginTop: 12, width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
                background: '#dc2626', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              Buy ZESA token
            </button>
          )}
        </div>
      </div>

      <WhyChooseUs />
      <WhatsAppBanner />
      <PartnersStrip />
      <Footer />
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: 999, border: 'none', fontSize: 12, fontWeight: 600,
        cursor: 'pointer', background: active ? 'white' : 'rgba(255,255,255,0.15)',
        color: active ? '#1e2460' : 'white', whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function ScrollArrow({ dir, onClick }: { dir: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={dir === 'left' ? 'Scroll left' : 'Scroll right'}
      style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: '50%', border: 'none',
        background: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', fontSize: 14,
      }}
    >
      {dir === 'left' ? '‹' : '›'}
    </button>
  );
}
